import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSpreadsheet, FileDown, UploadCloud, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMP, downloadBlob } from "@/lib/invoiceImport";

export function Step({ n, title, desc, children, testid }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-[#151D2E] p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3"><span className="w-7 h-7 rounded-full bg-amber-400 text-slate-950 font-black text-sm grid place-items-center">{n}</span><div><h2 className="font-bold text-base md:text-lg leading-tight">{title}</h2>{desc && <p className="text-xs text-slate-400">{desc}</p>}</div></div>
      {children}
    </section>
  );
}

export function TemplateStep() {
  const [busy, setBusy] = useState("");
  const dl = async (fmt) => { setBusy(fmt); try { await downloadBlob(`${IMP}/template?format=${fmt}`, `invoice_import_template.${fmt}`); } catch { toast.error("Download failed"); } finally { setBusy(""); } };
  return (
    <Step n={1} title="Download template" desc="Fill it in Excel — one invoice per row. Delete the yellow example rows before uploading." testid="import-step-template">
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => dl("xlsx")} disabled={!!busy} data-testid="import-template-xlsx" className="bg-emerald-600 hover:bg-emerald-500 text-white"><FileSpreadsheet className="w-4 h-4 mr-2" /> {busy === "xlsx" ? "Preparing…" : "Download Excel Template"}</Button>
        <Button onClick={() => dl("csv")} disabled={!!busy} variant="outline" data-testid="import-template-csv" className="border-slate-600"><FileDown className="w-4 h-4 mr-2" /> CSV template</Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Required columns: <span className="font-mono-num text-slate-300">invoice_number · invoice_date · customer_name · total</span>. Dates as YYYY-MM-DD. Payment status: Paid / Unpaid / Partially Paid / Overdue / Cancelled.</p>
    </Step>
  );
}

export function UploadStep({ onParsed, disabled }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const send = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xlsm|csv)$/i.test(file.name)) return toast.error("Please choose an .xlsx or .csv file");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await axios.post(`${IMP}/upload`, fd); onParsed(data); toast.success(`${data.summary.total} rows read from ${file.name}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not read the file"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <Step n={2} title="Upload your file" desc="Excel (.xlsx) or CSV (.csv). Column names are matched automatically — you can adjust them in the next step." testid="import-step-upload">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files?.[0]); }}
        onClick={() => !disabled && ref.current?.click()} data-testid="import-dropzone"
        className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 text-center cursor-pointer transition-colors ${drag ? "border-amber-400 bg-amber-400/10" : "border-slate-700 hover:border-slate-500"} ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        <UploadCloud className="w-8 h-8 mx-auto text-amber-300 mb-2" />
        <div className="font-bold">{busy ? "Reading file…" : "Drag your Excel or CSV file here"}</div>
        <div className="text-xs text-slate-400 mt-1">or <span className="text-amber-300 underline">choose a file</span> · up to 2,000 invoices</div>
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={(e) => send(e.target.files?.[0])} data-testid="import-file-input" />
      </div>
    </Step>
  );
}

export function MappingPanel({ imp, fields, onChange }) {
  const [open, setOpen] = useState(!!imp.summary.missing_required?.length);
  const [m, setM] = useState(imp.mapping);
  const [busy, setBusy] = useState(false);
  const apply = async () => { setBusy(true); try { const { data } = await axios.post(`${IMP}/${imp.id}/remap`, { mapping: m }); onChange(data); toast.success("Columns re-mapped"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-map"); } finally { setBusy(false); } };
  const reset = () => setM(imp.mapping);
  const mapped = Object.values(m).filter(Boolean).length;
  const unmapped = imp.headers.filter((h) => !Object.values(m).includes(h));
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#151D2E]" data-testid="import-mapping">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="import-mapping-toggle">
        <div><div className="font-bold text-sm">Detected column mapping <span className="text-slate-400 font-normal">· {mapped} of {fields.length} fields matched{unmapped.length ? ` · ${unmapped.length} column${unmapped.length === 1 ? "" : "s"} ignored` : ""}</span></div>
          {imp.summary.missing_required?.length > 0 && <div className="text-xs text-rose-300 mt-0.5" data-testid="import-mapping-missing">Required fields not mapped: {imp.summary.missing_required.join(", ")} — map them below</div>}</div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fields.map((f) => (
              <label key={f.key} className="text-xs text-slate-400 flex items-center gap-2 rounded-lg bg-slate-950/40 border border-slate-800 px-2.5 py-2">
                <span className="w-32 shrink-0 truncate">{f.label}{f.required && <span className="text-rose-300"> *</span>}</span><span className="text-slate-600">→</span>
                <select value={m[f.key] || ""} onChange={(e) => setM((x) => ({ ...x, [f.key]: e.target.value || null }))} data-testid={`import-map-${f.key}`} className="flex-1 min-w-0 bg-transparent text-slate-100 text-xs outline-none">
                  <option value="">— not in file —</option>{imp.headers.map((h) => <option key={h} value={h}>{h}</option>)}</select>
                {m[f.key] && <span className="text-emerald-300">✓</span>}
              </label>))}
          </div>
          <div className="flex gap-2"><Button size="sm" onClick={apply} disabled={busy} data-testid="import-mapping-apply" className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold">{busy ? "Applying…" : "Apply mapping"}</Button><Button size="sm" variant="ghost" onClick={reset} data-testid="import-mapping-reset" className="text-slate-400">Reset to auto</Button></div>
        </div>)}
    </div>
  );
}
